import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { useBooking } from "@/components/site/BookingContext";
import MenuOverlay from "@/components/site/MenuOverlay";
import { NAV_SECONDARY } from "@/lib/content";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { openBooking } = useBooking();

  useEffect(() => {
    let scrollEndTimer;
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      // backdrop-blur on a fixed header forces the browser to recompute the
      // blurred backdrop on the main thread every scroll frame — it can't
      // use the cheap compositor-only fast path a plain fixed element gets.
      // Under a fast/large scroll delta that shows up as the header
      // losing sync with the page for a frame (text from the section below
      // briefly visible through it). Dropping the blur while actively
      // scrolling (the near-opaque bg-white/95 alone already hides content
      // behind it almost entirely) and restoring it once scrolling settles
      // keeps the same resting look without paying that cost mid-scroll.
      setIsScrolling(true);
      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => setIsScrolling(false), 150);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(scrollEndTimer);
    };
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-[60] transition-all duration-500 ${
          scrolled
            ? `bg-white/95 border-b border-graymid shadow-sm ${isScrolling ? "" : "backdrop-blur-xl"}`
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="w-full max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <a href="/" data-testid="logo" className="flex items-center gap-2.5 group">
            <span className="relative flex h-7 w-7 items-center justify-center">
              <span className="absolute inset-0 rounded-full bg-navy-900/20 blur-md group-hover:bg-navy-900/35 transition-colors" />
              <span className="relative h-2.5 w-2.5 rounded-full bg-navy-900" />
            </span>
            <span className="font-display font-bold uppercase tracking-[0.06em] text-[1.1875rem] text-navy-900">
              Favor Studio
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {NAV_SECONDARY.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
                className="text-sm text-navy-900/70 hover:text-navy-900 transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-navy-900 hover:after:w-full after:transition-all after:duration-300"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <button
              onClick={openBooking}
              data-testid="header-book-btn"
              className="hidden sm:inline-flex items-center h-9 md:h-10 px-4 md:px-5 rounded-sm bg-navy-900 text-white text-sm font-semibold hover:bg-navy-950 transition-colors duration-300"
            >
              Book a Live Demo
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="menu-open-btn"
              aria-label="Open menu"
              className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-navy-900/15 hover:border-navy-900 hover:bg-navy-900/5 transition-colors"
            >
              <Menu className="w-5 h-5 text-navy-900" />
            </button>
          </div>
        </div>
      </header>

      <MenuOverlay open={menuOpen} onClose={() => setMenuOpen(false)} onBook={openBooking} />
    </>
  );
}
